import type { Format } from '@gg/shared';

/**
 * Cards of the home page's "Épreuves" section (docs/design/site/Main.dc.html): the event's
 * formats, from the API, grouped the way the design presents them.
 * - `cycling` / `running`: individual gravel and running formats;
 * - `duo`: mixed formats for pairs (Run & Ride), or mixed formats bought alone;
 * - `team`: other mixed team formats (12H Endurance).
 * Gravel or running team formats join their discipline's card.
 */
export type SiteGroup = 'cycling' | 'running' | 'duo' | 'team';
export const SITE_GROUPS: readonly SiteGroup[] = ['cycling', 'running', 'duo', 'team'];

export function siteGroupOf(f: Pick<Format, 'discipline' | 'registrationMode' | 'teamSizeMax'>) {
  if (f.discipline === 'gravel') return 'cycling';
  if (f.discipline === 'running') return 'running';
  return f.registrationMode === 'individual' || f.teamSizeMax === 2 ? 'duo' : 'team';
}

/** Active formats by card, in the admin's sort order; empty cards are left out. */
export function groupSiteFormats(formats: Format[]): { group: SiteGroup; formats: Format[] }[] {
  const active = formats.filter((f) => f.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  return SITE_GROUPS.map((group) => ({
    group,
    formats: active.filter((f) => siteGroupOf(f) === group),
  })).filter((g) => g.formats.length > 0);
}

/** End of the earliest early-bird tier still on sale (hero and section badge), or null. */
export function earlyBirdEnd(formats: Format[]): string | null {
  return (
    formats
      .filter(
        (f) =>
          f.isActive &&
          f.currentTier?.endsAt &&
          f.standardPriceCents !== null &&
          f.currentTier.priceCents < f.standardPriceCents,
      )
      .map((f) => f.currentTier!.endsAt!)
      .sort()[0] ?? null
  );
}
